import { observer } from 'mobx-react-lite';
import { motion } from 'motion/react';
import { useEffect } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { XStack } from '@/components/ui/layout/layout';
import { useHashRoute } from '@/hooks/useHashRoute';
import { uiStore } from '@/stores/ui.store';
import { trackEvent } from '@/utils/analytics';
import { classList } from '@/utils/cn';

import { USAGE_WARNINGS, useCreditUsage } from './hooks/use-credit-usage';
import { formatResetDate } from './usage-format';

const DANGER_PCT = USAGE_WARNINGS[USAGE_WARNINGS.length - 1].pct;

const activeThreshold = (
  usedPct: number,
  resetsAt: string,
  daysUntilReset: number
): number | null => {
  const crossed = USAGE_WARNINGS.filter(
    ({ pct, minDaysToReset }) =>
      usedPct >= pct && daysUntilReset >= minDaysToReset
  ).at(-1);
  if (!crossed) return null;
  return uiStore.isUsageWarningDismissed(resetsAt, crossed.pct)
    ? null
    : crossed.pct;
};

export const UsageWarningNotification = observer(
  ({ className }: { className?: string }) => {
    const { setHash } = useHashRoute();
    const {
      usedPct,
      spendableUsedPct,
      hasSpareCredits,
      resetsAt,
      daysUntilReset,
      hasUsageFigure,
    } = useCreditUsage();
    const threshold =
      hasUsageFigure && resetsAt && !hasSpareCredits
        ? activeThreshold(spendableUsedPct, resetsAt, daysUntilReset)
        : null;

    useEffect(() => {
      if (threshold !== null) trackEvent('USAGE_WARNING_SHOWN', { threshold });
    }, [threshold]);

    if (threshold === null || !resetsAt) return null;

    const isDanger = threshold === DANGER_PCT;

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        <XStack
          className={classList(
            'items-center justify-between gap-3',
            isDanger
              ? 'bg-red-50/20 dark:bg-red-950/20'
              : 'bg-amber-50/20 dark:bg-amber-950/20',
            'rounded-t-xl',
            'p-1.5',
            'border-b dark:border-border-strong border-border',
            className
          )}
        >
          <XStack className="items-center gap-2 px-2">
            <Icons.warnTriangle className="size-4 text-foreground-muted" />
            <span className="text-sm text-foreground-subtle">
              {usedPct >= 100
                ? 'You have used all of your monthly usage.'
                : `You have used ${usedPct}% of your monthly usage.`}{' '}
              Resets on {formatResetDate(resetsAt)}.
            </span>
          </XStack>
          <XStack className="items-center gap-1 shrink-0">
            <Button size="sm" onClick={() => setHash('settings/Usage')}>
              View usage
            </Button>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Dismiss usage warning"
              className="rounded-full"
              onClick={() => {
                trackEvent('USAGE_WARNING_DISMISSED', { threshold });
                uiStore.dismissUsageWarning(resetsAt, threshold);
              }}
            >
              <Icons.cancel className="size-4" />
            </Button>
          </XStack>
        </XStack>
      </motion.div>
    );
  }
);
